import { Dispatch, SetStateAction, useMemo } from 'react';

import { useEvent } from '@restDataAccess';
import {
  Column,
  FlexAligner,
  LoadingBubbles,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Row,
  Typography,
} from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { CustomReactJson } from '@/components/customReactJson';
import { ModalWithDefaultZIndex } from '@/styles';
import { responsive } from '@/utils';

import { useInventoryContext } from './InventoryContext';

type EventModalViewProps = {
  eventId?: string;
  setEventId: Dispatch<SetStateAction<string>>;
};
export const EventModalView = ({ eventId = '', setEventId }: EventModalViewProps) => {
  const { t } = useTranslation(['common', 'eventStream']);
  const { data: event, isLoading } = useEvent(eventId || '');
  const { getInventoryNameById, closeModal } = useInventoryContext();
  const inventoryName: string | undefined = useMemo(() => {
    if (!event?.inventoryId) return 'n/a';
    if (event?.inventoryId) {
      return getInventoryNameById(event.inventoryId);
    }
  }, [event?.inventoryId, getInventoryNameById]);
  if (!isLoading) {
    return (
      <ModalWithDefaultZIndex
        open={!!eventId}
        closeOnEscapeClick
        closeOnBackdropClick
        onClose={() => setEventId('')}
      >
        <ModalHeader title={t('common:event')} onClose={closeModal}></ModalHeader>
        <ModalContent>
          {isLoading && <LoadingBubbles />}
          <>
            <Row gap='1.25rem'>
              <FlexAligner spaceBetween={true}>
                <Typography>{t('common:id')}</Typography>
                <Typography>{eventId}</Typography>
              </FlexAligner>
            </Row>
            <Row gap='1.25rem'>
              <FlexAligner spaceBetween={true}>
                <Typography>{t('eventStream:headers.type')}</Typography>
                <Typography>{event?.type}</Typography>
              </FlexAligner>
            </Row>
            <Row gap='1.25rem'>
              <FlexAligner spaceBetween={true}>
                <Typography>{t('eventStream:headers.sender')}</Typography>
                <Typography>{event?.sender}</Typography>
              </FlexAligner>
            </Row>
            <Row gap='1.25rem'>
              <FlexAligner spaceBetween={true}>
                <Typography>{t('eventStream:headers.source')}</Typography>
                <Typography>{event?.inventoryId}</Typography>
              </FlexAligner>
            </Row>
            <Row gap='1.25rem'>
              <FlexAligner spaceBetween={true}>
                <Typography>{t('eventStream:headers.inventoryName')}</Typography>
                <Typography>{inventoryName}</Typography>
              </FlexAligner>
            </Row>
            <Column style={{ paddingTop: `${responsive.spacing['4xlarge']}` }}>
              <CustomReactJson src={event?.data || {}} collapsed={1} />
            </Column>
          </>
        </ModalContent>

        <ModalFooter actionButtons={[{ label: t('close'), onClick: closeModal }]} />
      </ModalWithDefaultZIndex>
    );
  }
};
